// Returns the offset of `timeZone` from UTC at `date`, in ms.
// Positive for zones east of UTC.
function getZoneOffsetMs(date: Date, timeZone: string): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts = Object.fromEntries(
    dtf
      .formatToParts(date)
      .filter((p) => p.type !== 'literal')
      .map((p) => [p.type, p.value]),
  );
  const asLocalUTC = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );
  return asLocalUTC - date.getTime();
}

// Convert a wall-clock string like "2026-05-22T17:00" interpreted in
// `timeZone` into a UTC instant. Iterates twice to be DST-correct near
// transitions.
export function zonedWallClockToInstant(wallClock: string, timeZone: string): Date {
  const m = wallClock.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) throw new Error(`Invalid datetime-local string: ${wallClock}`);
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const h = Number(m[4]);
  const mi = Number(m[5]);

  const naive = Date.UTC(y, mo - 1, d, h, mi);
  let offset = getZoneOffsetMs(new Date(naive), timeZone);
  // Re-resolve once more in case the first guess landed in a different
  // DST window than the corrected instant.
  offset = getZoneOffsetMs(new Date(naive - offset), timeZone);
  return new Date(naive - offset);
}

// Inverse: an ISO instant rendered as a wall-clock string in the given zone,
// suitable for an `<input type="datetime-local">` value.
export function instantToZonedWallClock(iso: string | Date, timeZone: string): string {
  const date = iso instanceof Date ? iso : new Date(iso);
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  const parts = Object.fromEntries(
    dtf
      .formatToParts(date)
      .filter((p) => p.type !== 'literal')
      .map((p) => [p.type, p.value]),
  );
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

export function formatTimeInZone(
  iso: string | Date,
  timeZone: string,
  opts: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' },
): string {
  const date = iso instanceof Date ? iso : new Date(iso);
  return new Intl.DateTimeFormat('en-US', { ...opts, timeZone }).format(date);
}

export function formatDateInZone(
  iso: string | Date,
  timeZone: string,
  opts: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric' },
): string {
  const date = iso instanceof Date ? iso : new Date(iso);
  return new Intl.DateTimeFormat('en-US', { ...opts, timeZone }).format(date);
}

export function browserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

// A trimmed list of common IANA zones for the picker. Users can type to
// search; this just gives sensible defaults at the top.
export const COMMON_TIMEZONES = [
  'America/Los_Angeles',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
  'America/Toronto',
  'America/Mexico_City',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Athens',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Pacific/Auckland',
  'UTC',
] as const;
